import React, { FunctionComponent, useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import dayjs, { Dayjs } from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MaterialTable from "@material-table/core";
import useStyles from "./styles";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { Alert } from "@material-ui/lab";
import { Check } from "@mui/icons-material";
import { get, post } from "../../api/api";
import DateRangePicker from "../../components/inputs/daterangepickerv2";
import { Currency } from "../../components/misc/Currency";
import { Authenticated, hasRole } from "../../models/User";
import { UserContext } from "../../UserProvider";

import {
  DialogActions,
  DialogContentText,
  DialogTitle
} from "@material-ui/core";
import { useI18n } from "../../hooks/useI18n";
import message from "material-ui/svg-icons/communication/message";

const fetchReports = async (
  startDate: string | null,
  endDate: string | null
) => {
  if (!startDate || !endDate) {
    return [];
  }
  try {
    const response = await get(
      `/v1/mpos/eod?periodFrom=${encodeURIComponent(
        startDate
      )}&periodTo=${encodeURIComponent(endDate)}`
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch reports: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching reports:", error);
    return [];
  }
};

export const approveRequest = async (eod: any) => {
  const response = await post("/v1/mpos/approve-eod", eod);
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail);
  }

  const data = await response.json();
  return data;
};

export const MobilePOSCashierReports: FunctionComponent = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallDown = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useContext(UserContext) as Authenticated;
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const formattedStartDate = startDate
    ? startDate.format("YYYY-MM-DDTHH:mm:ssZ")
    : null;
  const formattedEndDate = endDate
    ? endDate.format("YYYY-MM-DDTHH:mm:ssZ")
    : null;

  const [state, setState] = useState<any>({
    showApproveEODDialog: false,
    date: ""
  });

  const [showAmountWarning, setShowAmountWarning] = useState(false);

  const [eod, setEod] = useState({
    status: "",
    storeId: "",
    paymentProvider: { id: "", name: "" },
    day: "",
    amount: 0,
    enteredAmount: undefined as number | undefined,
    nrOfReceipts: "",
    paymentMethods: "",
    comment: "",
    forceSaveReport: false
  });

  const handleDateRangeChange = ([start, end]: [
    Dayjs | null,
    Dayjs | null
  ]) => {
    setStartDate(start);
    setEndDate(end);
  };

  const { data, isLoading, isError } = useQuery(
    ["cashierReports", formattedStartDate, formattedEndDate],
    () => fetchReports(formattedStartDate, formattedEndDate),
    {
      staleTime: Infinity,
      enabled: !!startDate && !!endDate
    }
  );

  const transformData = (data: any[]) => {
    if (!data || data.length === 0) return [];

    const groupedData = data.reduce((acc, entry) => {
      const { day, nrOfReceipts, paymentProvider, amount, enteredAmount } =
        entry;

      if (!acc[day]) {
        acc[day] = {
          day,
          nrOfReceipts: 0,
          paymentMethods: new Set(),
          status: entry.status,
          amount: 0,
          enteredAmount: 0,
          originalEntries: []
        };
      }

      acc[day].nrOfReceipts += nrOfReceipts;
      acc[day].paymentMethods.add(paymentProvider.name);

      const amountToAdd =
        enteredAmount != null && enteredAmount !== 0
          ? parseFloat(enteredAmount)
          : parseFloat(amount || 0);

      acc[day].amount += amountToAdd;

      if (enteredAmount != null && enteredAmount !== 0) {
        acc[day].enteredAmount += parseFloat(enteredAmount);
      }

      acc[day].originalEntries.push(entry);
      return acc;
    }, {} as Record<string, any>);

    return Object.values(groupedData).map((entry: any) => ({
      day: entry.day,
      nrOfReceipts: entry.nrOfReceipts,
      paymentMethods: Array.from(entry.paymentMethods).join(", "),
      status: entry.status,
      amount: entry.amount,
      originalEntries: entry.originalEntries
    }));
  };

  const getPaymentMethodAmounts = (entries: any[]) => {
    const methodAmounts: Record<string, number> = {};
    entries.forEach(entry => {
      const methodName = entry.paymentProvider.name;
      if (!methodAmounts[methodName]) {
        methodAmounts[methodName] = 0;
      }
      methodAmounts[methodName] += parseFloat(entry.amount || 0);
    });

    return methodAmounts;
  };

  const getPaymentMethodEnteredAmounts = (entries: any[]) => {
    const methodEntered: Record<string, number> = {};
    entries.forEach(entry => {
      const methodName = entry.paymentProvider.name;
      if (!methodEntered[methodName]) {
        methodEntered[methodName] = 0;
      }
      if (entry.enteredAmount != null && entry.enteredAmount !== 0) {
        methodEntered[methodName] += parseFloat(entry.enteredAmount || 0);
      }
    });
    return methodEntered;
  };

  const [approvalError, setApprovalError] = useState<string | null>(null);

  const { mutate, isLoading: approveRequestLoading } = useMutation(
    approveRequest,
    {
      onSuccess: () => {
        setState({ ...state, showApproveEODDialog: false });
        setEod({
          status: "",
          storeId: "",
          paymentProvider: { id: "", name: "" },
          day: "",
          amount: 0,
          enteredAmount: undefined,
          nrOfReceipts: "",
          paymentMethods: "",
          comment: "",
          forceSaveReport: false
        });
        setPaymentMethodAmounts({});
        setPaymentMethodEnteredAmounts({});
        setApprovalError(null);
        queryClient.refetchQueries([
          "cashierReports",
          formattedStartDate,
          formattedEndDate
        ]);
      },
      onError: (err: Error | any) => {
        console.error("Approval failed:", err);
        setApprovalError(
          "Failed to approve request. Please try again or contact support."
        );
      }
    }
  );

  const [paymentMethodAmounts, setPaymentMethodAmounts] = useState<
    Record<string, number>
  >({});
  const [paymentMethodEnteredAmounts, setPaymentMethodEnteredAmounts] =
    useState<Record<string, number>>({});

  return (
    <div>
      <div className={classes.dialogHeader}>
        <AssignmentIcon className={classes.receiptIcon} />
        <Typography
          variant="button"
          component="span"
          style={{ fontWeight: 500, marginRight: 16 }}
        >
          {t("common.eodForPeriod")}
        </Typography>
        <div className={classes.datePickerWrapper}>
          <DateRangePicker
            onDateRangeChange={handleDateRangeChange}
            shortcutsToExclude={[t("common.today")]}
            defaultSelectedShortcut={t("common.thisWeek")}
            maxDate={dayjs().subtract(1, "day")}
          />
        </div>
      </div>
      <MaterialTable
        isLoading={isLoading}
        style={{
          marginBottom: 20,
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          borderRadius: 8
        }}
        title=""
        columns={[
          {
            title: t("common.tableHeaders.day"),
            field: "day",
            cellStyle: { padding: isSmallDown ? "8px" : "" }
          },
          {
            title: t("common.tableHeaders.nrOfReceipts"),
            field: "nrOfReceipts",
            cellStyle: { padding: isSmallDown ? "8px" : "" }
          },
          {
            title: t("common.tableHeaders.paymentMethods"),
            field: "paymentMethods",
            render: (rowData: any) => {
              if (!rowData.paymentMethods) return null;

              const methods = rowData.paymentMethods
                .split(",")
                .map((m: string) => m.trim());

              const translated = methods.map((m: string) => {
                switch (m.toLowerCase()) {
                  case "cash":
                    return t("common.cash");
                  case "swish":
                    return t("common.swish");
                  default:
                    return m;
                }
              });

              return <span>{translated.join(", ")}</span>;
            }
          },
          {
            title: t("common.tableHeaders.totalAmount"),
            field: "amount",
            render: (rowData: any) => {
              return (
                <span>
                  <Currency value={parseFloat(rowData.amount)} />
                </span>
              );
            },
            cellStyle: { padding: isSmallDown ? "8px" : "" }
          },
          {
            cellStyle: { padding: isSmallDown ? "8px" : "" },
            render: (rowData: any) => (
              <div>
                {rowData.status !== "Approved" ? (
                  hasRole(user, ["CashierReports"]) ? (
                    <Tooltip title={t("common.tooltip.clickToApprove")}>
                      <Button
                        style={{ minWidth: 120, justifyContent: "center" }}
                        startIcon={<Check />}
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          const methodAmounts = getPaymentMethodAmounts(
                            rowData.originalEntries
                          );
                          const methodEntered = getPaymentMethodEnteredAmounts(
                            rowData.originalEntries
                          );

                          setPaymentMethodAmounts(methodAmounts);
                          setPaymentMethodEnteredAmounts(methodEntered);

                          setState({
                            ...state,
                            showApproveEODDialog: true,
                            date: rowData.day
                          });
                          setEod({
                            ...eod,
                            storeId: rowData.originalEntries[0]?.storeId || "",
                            paymentMethods: rowData.paymentMethods,
                            day: rowData.day,
                            amount: rowData.amount || 0
                          });
                        }}
                      >
                        {t("common.approveEOD")}
                      </Button>
                    </Tooltip>
                  ) : null
                ) : (
                  <Tooltip title={t("common.messages.alreadyApproved")}>
                    <Button
                      style={{ minWidth: 120, justifyContent: "center" }}
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        const methodAmounts = getPaymentMethodAmounts(
                          rowData.originalEntries
                        );
                        const methodEntered = getPaymentMethodEnteredAmounts(
                          rowData.originalEntries
                        );

                        let cashComment = "";
                        if (rowData.status === "Approved") {
                          const cashEntry = rowData.originalEntries.find(
                            (entry: any) =>
                              entry.paymentProvider.name === "Cash"
                          );
                          if (cashEntry && cashEntry.comment) {
                            cashComment = cashEntry.comment;
                          }
                        }

                        setPaymentMethodAmounts(methodAmounts);
                        setPaymentMethodEnteredAmounts(methodEntered);
                        setState({
                          ...state,
                          showApproveEODDialog: true,
                          date: rowData.day
                        });
                        setEod({
                          ...eod,
                          status: rowData.status,
                          storeId: rowData.originalEntries[0]?.storeId || "",
                          paymentMethods: rowData.paymentMethods,
                          day: rowData.day,
                          amount: rowData.amount || 0,
                          enteredAmount: methodEntered["Cash"] ?? undefined,
                          comment: cashComment
                        });
                      }}
                    >
                      {t("common.showEOD")}
                    </Button>
                  </Tooltip>
                )}
              </div>
            )
          }
        ]}
        data={data ? transformData(data) : []}
        options={{
          pageSize: 10,
          headerStyle: {
            backgroundColor: "#f5f5f5",
            color: "#424242",
            fontWeight: "bold",
            padding: "16px 8px"
          },
          rowStyle: (rowData: any) => ({
            backgroundColor: rowData.status === "Approved" ? "#d4edda" : "#fff"
          }),
          searchFieldStyle: {
            borderRadius: 4,
            padding: "8px 12px",
            border: "1px solid #e0e0e0"
          },
          actionsColumnIndex: -1,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [5, 10, 20, 50]
        }}
        localization={{
          toolbar: {
            searchPlaceholder: t("common.search"),
            searchTooltip: t("common.searchTooltip")
          },
          pagination: {
            labelRowsSelect: t("rows"),
            labelDisplayedRows: t("displayedRowsTemplate"),
            firstTooltip: t("first"),
            previousTooltip: t("prev"),
            nextTooltip: t("next"),
            lastTooltip: t("last")
          },
          body: {
            emptyDataSourceMessage: t("noRecords")
          }
        }}
      />

      <Dialog
        fullWidth
        maxWidth="sm"
        open={state.showApproveEODDialog}
        onClose={() => {
          setState({
            ...state,
            showApproveEODDialog: false
          });
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className={classes.dialogHeader}>
          <AssignmentIcon className={classes.receiptIcon} />
          <Typography
            variant="button"
            component="span"
            style={{ fontWeight: 500, marginRight: 16 }}
          >
            {t("common.approveEODForPeriod")} {state.date}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, showApproveEODDialog: false });
              setApprovalError(null);
              setEod({
                status: "",
                storeId: "",
                day: "",
                amount: 0,
                paymentProvider: { id: "", name: "" },
                enteredAmount: undefined,
                nrOfReceipts: "",
                paymentMethods: "",
                comment: "",
                forceSaveReport: false
              });
              setPaymentMethodAmounts({});
              setPaymentMethodEnteredAmounts({});
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent dividers>
          <div>
            <div className={classes.summaryContainer}>
              <Typography variant="subtitle1" className={classes.summaryTitle}>
                {t("common.summary")}
              </Typography>
              <div
                style={{
                  marginTop: 24,
                  fontSize: "14px",
                  lineHeight: 1.6
                }}
              >
                {eod.paymentMethods &&
                  eod.paymentMethods.split(",").map((method, index) => {
                    const methodName = method.trim();

                    const systemAmount = paymentMethodAmounts[methodName] || 0;
                    const enteredForMethod =
                      paymentMethodEnteredAmounts[methodName];
                    const isCash = methodName === "Cash";
                    const hasEntered = enteredForMethod != null;
                    const diff = hasEntered
                      ? enteredForMethod - systemAmount
                      : 0;
                    const differs =
                      isCash &&
                      enteredForMethod != null &&
                      Math.abs(enteredForMethod - systemAmount) > 0.01;

                    const hasComment =
                      eod.status === "Approved" && isCash && eod.comment;
                    const prettyName =
                      methodName.toLowerCase() === "cash"
                        ? t("common.cash")
                        : methodName.toLowerCase() === "swish"
                        ? t("common.swish")
                        : methodName;

                    return (
                      <div key={index} style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            color: "#2c3e50",
                            fontWeight: 500
                          }}
                        >
                          <span>{prettyName}</span>
                          <span>
                            <Currency value={systemAmount} />
                          </span>
                        </div>

                        {differs && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 12,
                              color: "#7f8c8d",
                              paddingLeft: 4,
                              marginTop: 2
                            }}
                          >
                            <span>{t("common.enteredAmount")}:</span>
                            <span>
                              <Currency value={enteredForMethod!} />
                            </span>
                          </div>
                        )}
                        {differs && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 12,
                              paddingLeft: 4,
                              marginTop: 2,
                              color:
                                Math.abs(diff) > 0.01 ? "#c0392b" : "#7f8c8d"
                            }}
                          >
                            <span>{t("common.difference")}:</span>
                            <span
                              style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {diff >= 0 ? "+" : "−"}{" "}
                              <Currency value={Math.abs(diff)} />
                            </span>
                          </div>
                        )}

                        {hasComment && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#7f8c8d",
                              fontStyle: "italic",
                              marginTop: 2,
                              paddingLeft: 4
                            }}
                          >
                            {t("common.eodComment")}: {eod.comment}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
            {eod.status !== "Approved" && (
              <>
                {eod.paymentMethods.includes("Cash") && (
                  <>
                    <div className={classes.inputField}>
                      <TextField
                        type="number"
                        fullWidth
                        value={eod.enteredAmount ?? ""}
                        onChange={event => {
                          const value = event.target.value;

                          if (value.length > 1 && value.startsWith("0")) {
                            return;
                          }

                          setEod({
                            ...eod,
                            enteredAmount:
                              value === "" ? undefined : parseFloat(value)
                          });
                        }}
                        variant="outlined"
                        label={t("common.formFields.cashPaymentAmount")}
                        InputProps={{
                          inputMode: "numeric"
                        }}
                      />
                    </div>
                  </>
                )}

                <div className={classes.buttonContainer}>
                  {!approvalError ? (
                    <Button
                      className={
                        approveRequestLoading ||
                        (eod.paymentMethods.includes("Cash") &&
                          eod.enteredAmount === undefined)
                          ? classes.approveButtonDisabled
                          : classes.approveButton
                      }
                      onClick={() => {
                        const actualCashAmount =
                          paymentMethodAmounts["Cash"] || 0;
                        const enteredAmount = eod.enteredAmount ?? 0;
                        if (
                          eod.paymentMethods.includes("Cash") &&
                          Math.abs(actualCashAmount - enteredAmount) > 0.01
                        ) {
                          setShowAmountWarning(true);
                        } else {
                          mutate(eod);
                        }
                      }}
                      disabled={
                        approveRequestLoading ||
                        (eod.paymentMethods.includes("Cash") &&
                          eod.enteredAmount === undefined)
                      }
                    >
                      {approveRequestLoading
                        ? t("common.approvingEOD")
                        : t("common.approveEOD")}
                    </Button>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: 30
                      }}
                    >
                      <Alert severity="error">{approvalError}</Alert>
                      <Button
                        className={classes.approveButton}
                        onClick={() => setApprovalError(null)}
                        style={{ marginTop: 10 }}
                      >
                        {t("common.approveCountAgain")}
                      </Button>
                      <Button
                        className={
                          !eod.comment
                            ? classes.approveButtonDisabled
                            : classes.saveReportAnywayButton
                        }
                        style={{ marginTop: 10 }}
                        disabled={!eod.comment}
                        onClick={() => {
                          setApprovalError(null);
                          setEod(prevEod => {
                            const updatedEod = {
                              ...prevEod,
                              forceSaveReport: true
                            };
                            mutate(updatedEod);
                            return updatedEod;
                          });
                        }}
                      >
                        {t("common.saveEODAnyway")}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showAmountWarning}
        onClose={() => setShowAmountWarning(false)}
        aria-labelledby="amount-warning-title"
        aria-describedby="amount-warning-description"
      >
        <DialogTitle id="amount-warning-title">
          {t("common.cashAmountMismatch")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="amount-warning-description">
            {t("common.expectedAmount")}:{" "}
            <Currency value={paymentMethodAmounts["Cash"] || 0} />
            <br />
            {t("common.enteredAmount")}:{" "}
            <Currency value={eod.enteredAmount || 0} />
            <br />
            <br />
            {(() => {
              const actualCashAmount = paymentMethodAmounts["Cash"] || 0;
              const enteredAmount = eod.enteredAmount || 0;
              const discrepancy = Math.abs(actualCashAmount - enteredAmount);

              if (discrepancy >= 500) {
                return (
                  <>
                    {t("common.cashAmountMismatchMessage")}
                    <br />
                    <br />
                    <TextField
                      fullWidth
                      value={eod.comment}
                      onChange={event => {
                        setEod({ ...eod, comment: event.target.value });
                      }}
                      variant="outlined"
                      label={t("common.formFields.commentsMax14")}
                      inputProps={{
                        maxLength: 14
                      }}
                      size="small"
                      style={{ marginBottom: 16 }}
                    />
                  </>
                );
              } else {
                const msg = t("common.cashAmountMismatchNoCommentMessage");
                return msg;
              }
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAmountWarning(false)} color="primary">
            {(() => {
              const actualCashAmount = paymentMethodAmounts["Cash"] || 0;
              const enteredAmount = eod.enteredAmount || 0;
              const discrepancy = Math.abs(actualCashAmount - enteredAmount);
              return discrepancy >= 500 ? "Cancel" : "No";
            })()}
          </Button>
          <Button
            onClick={() => {
              const actualCashAmount = paymentMethodAmounts["Cash"] || 0;
              const enteredAmount = eod.enteredAmount || 0;
              const discrepancy = Math.abs(actualCashAmount - enteredAmount);

              if (discrepancy >= 500 && !eod.comment) {
                return;
              }

              setShowAmountWarning(false);
              mutate({
                ...eod,
                forceSaveReport: true
              });
            }}
            color="primary"
            autoFocus
            disabled={(() => {
              const actualCashAmount = paymentMethodAmounts["Cash"] || 0;
              const enteredAmount = eod.enteredAmount || 0;
              const discrepancy = Math.abs(actualCashAmount - enteredAmount);
              return discrepancy >= 500 && !eod.comment;
            })()}
          >
            {(() => {
              const actualCashAmount = paymentMethodAmounts["Cash"] || 0;
              const enteredAmount = eod.enteredAmount || 0;
              const discrepancy = Math.abs(actualCashAmount - enteredAmount);
              return discrepancy >= 500 ? "Proceed" : "Yes";
            })()}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MobilePOSCashierReports;
